import tw from 'twin.macro';
import { withProperties } from '$/utils/type';
import React, { ReactElement } from 'react';
import {
  Manager as PopperManager,
  Popper,
  PopperChildrenProps,
  Reference as PopperReference,
} from 'react-popper';
import type { State, Placement, Modifier } from '@popperjs/core';
import { useOnClickOutside } from '$/hooks/useOnClickOutside';
import { css } from '@emotion/react';
import { useHasChangedShallow } from '$/hooks/useHasChanged';

type DropdownContextState = {
  open?: boolean;
};

const DropdownContext = React.createContext<DropdownContextState>({});

export const useDropdownContext = (): DropdownContextState => React.useContext(DropdownContext);

interface DropdownProps {
  isOpen?: boolean;
  closeOnSelect?: boolean;
  optionsClass?: string;
  className?: string;
  onOpen?: () => void;
  onClose?: () => void;
  children?: React.ReactNode;
}
export interface DropdownPropsRef {
  open: () => void;
  close: () => void;
}

const DropdownComponent = React.forwardRef<DropdownPropsRef, DropdownProps>((props, ref) => {
  const { closeOnSelect = true, optionsClass = '' } = props;
  const [open, setOpen] = React.useState(Boolean(props.isOpen));

  const toggleOpen = React.useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const onClose = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  React.useEffect(() => {
    if (!props.isOpen) return;
    setOpen(true);
  }, [props.isOpen]);

  React.useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
  }));

  const ponOpen = props.onOpen;
  const ponClose = props.onClose;
  const openChanged = useHasChangedShallow([open], { skipInitial: true });
  React.useEffect(() => {
    if (!openChanged) return;
    if (open) {
      ponOpen?.();
    } else {
      ponClose?.();
    }
  }, [open, ponOpen, ponClose, openChanged]);

  const onClickOusideRef = useOnClickOutside(onClose);

  return (
    <DropdownContext.Provider value={{ open }}>
      <PopperManager>
        <div
          className={`dropdown ${props.className}`}
          css={css`
            position: relative;
          `}
          ref={onClickOusideRef}
        >
          <div onClick={toggleOpen} className='flex cursor-pointer select-none'>
            {React.Children.toArray(props.children).filter(
              (child: any) => child.type.displayName === 'Dropdown.Reference'
            )}
          </div>
          <div
            className={`${optionsClass} ${open ? '' : 'hidden'}`}
            onClick={closeOnSelect ? onClose : undefined}
          >
            {React.Children.toArray(props.children).filter(
              (child: any) => child.type.displayName === 'Dropdown.Options'
            )}
          </div>
        </div>
      </PopperManager>
    </DropdownContext.Provider>
  );
});

const Reference = ({ children }: any) => (
  <PopperReference>
    {({ ref: popperTargetRef }: any) => (
      <div
        className='dropdown-reference text-color-input-field w-full inline-block'
        ref={popperTargetRef}
      >
        {children}
      </div>
    )}
  </PopperReference>
);

Reference.displayName = 'Dropdown.Reference';

interface OptionsProps {
  children: any;
  fullWidth?: boolean;
  fullHeight?: boolean;
  scroll?: boolean;
  cursorStyle?: string;
  className?: string;
  placement?: Placement;
  modifiers?: Partial<Modifier<any, any>>[];
  style?: React.CSSProperties;
}

/*
  export type Placement = 'auto-start'
    | 'auto'
    | 'auto-end'
    | 'top-start'
    | 'top'
    | 'top-end'
    | 'right-start'
    | 'right'
    | 'right-end'
    | 'bottom-end'
    | 'bottom'
    | 'bottom-start'
    | 'left-end'
    | 'left'
    | 'left-start';
*/

interface ContentWrapperProps extends Omit<OptionsProps, 'style'> {
  optionsStyle?: React.CSSProperties;
}

const ContentWrapper = React.forwardRef<HTMLDivElement, PopperChildrenProps & ContentWrapperProps>(
  (
    {
      children,
      placement,
      cursorStyle,
      fullHeight,
      className,
      update,
      style: popperStyle,
      optionsStyle,
      modifiers,
      scroll,
    },
    ref
  ) => {
    const { open } = useDropdownContext();
    const [opacity, setOpacity] = React.useState(
      Boolean(modifiers?.find((m) => m.name === 'computeStyles' && m.enabled)) ? 1 : 0
    );

    React.useEffect(() => {
      (async () => {
        // Override computeStyle will disabled auto calculation of popper
        // So we don't need to run scheduleUpdate to calculate
        if (modifiers?.find((m) => m.name === 'computeStyles' && m.enabled)) return;
        if (!open) {
          setOpacity(0);
          return;
        }
        await update();
        setOpacity(1);
      })();
    }, [open, update, modifiers]);

    return (
      <div
        className={`${className} ${cursorStyle ? cursorStyle : 'cursor-pointer '}`}
        css={[
          tw`flex flex-col justify-between z-dropdown-menu my-1 text-sm font-light text-color-input-option transition-opacity duration-100 rounded-lg shadow-lg bg-white border border-gray-10`,
          scroll && !fullHeight && tw`max-h-12e overflow-auto`,
          css`
            > *:first-of-type {
              border-top-left-radius: 0.5rem;
              border-top-right-radius: 0.5rem;
              overflow: hidden;
            }

            > *:last-child {
              border-bottom-left-radius: 0.5rem;
              border-bottom-right-radius: 0.5rem;
              overflow: hidden;
            }
          `,
        ]}
        ref={ref}
        style={{ ...optionsStyle, ...popperStyle, opacity }}
        data-placement={placement}
      >
        {children}
      </div>
    );
  }
);

const Options: React.FC<OptionsProps> & {
  Item: React.FC<{
    className?: string;
    onClick?: () => void;
    id?: string;
    children: React.ReactNode;
  }>;
} = ({ style: optionsStyle, ...props }) => {
  const computeStylesModifier = React.useMemo(() => {
    let computeStyles = props.modifiers?.find((m) => m.name === 'computeStyles');
    if (props.placement === 'top') {
      computeStyles = {
        name: 'computeStyles',
        enabled: true,
        options: {
          gpuAcceleration: false, // true by default
        },
        fn: ({ state }: { state: State }) => {
          state.elements.popper.style.bottom = `${state.rects.reference.height}px`;
          state.elements.popper.style.transform = `translateX(-50%)`;
          state.elements.popper.style.left = '50%';
          state.elements.popper.style.position = 'absolute';
        },
      };
    }
    return computeStyles;
  }, [props.modifiers, props.placement]);

  return (
    <Popper
      placement={props.placement}
      modifiers={[
        ...(props.modifiers ?? []),
        computeStylesModifier ?? {},
        {
          name: 'fullWidth',
          enabled: props.fullWidth,
          phase: 'main',
          fn: ({ state }: { state: State }): void => {
            const width = state.rects.reference.width;
            const left = state.rects.reference.x;
            state.elements.popper.style.left = `${left}px`;
            state.elements.popper.style.width = `${Math.round(width)}px`;
          },
        },
      ]}
    >
      {(childProps): React.ReactElement => (
        <ContentWrapper {...childProps} {...props} optionsStyle={optionsStyle}>
          {props.children}
        </ContentWrapper>
      )}
    </Popper>
  );
};

Options.displayName = 'Dropdown.Options';

Options.Item = ({ children, className, id, ...props }): ReactElement => {
  return (
    <div
      {...props}
      id={id}
      css={tw`px-5 h-8 hover:bg-gray-200 hover:text-color-input-field whitespace-nowrap flex items-center`}
      className={className}
    >
      {children}
    </div>
  );
};

export default withProperties(DropdownComponent, { Options, Reference });
